import React, { useCallback } from "react";
import PropTypes from "prop-types";
import classnames from "classnames";

import Context from "shared/util/Context";
import MessengerUserRow from "apps/messenger/components/shared/MessengerUserRow";
import MessagePane from "apps/messenger/components/MainWindow/MessagePane";
import EmptyState from "apps/messenger/components/MainWindow/EmptyState";
import Banners from "apps/messenger/components/MainWindow/Banners";
import Attachments from "apps/messenger/components/MainWindow/Attachments";
import Composer from "apps/messenger/components/MainWindow/Composer";
import Toolbar from "apps/messenger/components/MainWindow/Toolbar";
import ConnectionsLayer from "apps/messenger/components/Connections";
import { mapDraftToConnectionLayer, shouldShowConnectionLayer } from "apps/messenger/util/helpers";
import Overlay from "shared/components/FullscreenOverlay";
import _str from "shared/util/_str";

import { WINDOW_STATE } from "apps/messenger/util/consts";

import "apps/messenger/components/MainWindow/index.scss";

const isDesktop = process.env.PLATFORM === "desktop";

const MainWindow = ({
	openConversation,
	minimizeConversation,
	closeConversation,
	sendMessage,
	onReport,
	onUnblock,
	onDismissContentWarning,
	onMoveToSMS,
	loadMessages,
	updateDraft,

	error,
	draft,
	state,
	fields: user,
	nway,
	messages,
	unreadCount,
	totalMessages,
	loadingMessages,
	likedThisSession,
	userInstructions,
	hasInboxCapacity,
	feedbackAgent,
	canMessage,
	showScammerWarning,
	showContentWarning,
	showMoveToSMS,
	moveToSMSLoading,
}) => {
	const mainWindowClasses = classnames(
		"messenger-main-window",
		`messenger-main-window--${state}`,
	);

	const userid = user.userid;
	const username = user.userinfo.displayname;
	const draftText = draft ? draft.body || "" : "";
	const draftComment = draft ? draft.profileComment : null;

	const hasMessages = messages.length > 0;

	// Reasons to show a banner:
	const showFeedbackAgentWarning = feedbackAgent;
	const showInactiveWarning = user.inactive;
	const showFullInboxWarning = !hasInboxCapacity;

	// Reasons to hide messages:
	const messageISent = hasMessages && messages.find(({ from }) => from !== userid);
	const hasSentAMessage = !!messageISent;
	const showOcsBlock = hasSentAMessage && !user.likes.mutual_like && nway === 1;
	const showUserBlock = user.blocked;

	// Reasons to hide send:
	const canShowSend = !showUserBlock && !showOcsBlock && canMessage && hasInboxCapacity;

	// Reasons to disable send:
	const isMinimized = state === WINDOW_STATE.Minimized;
	const isSendDisabled = isMinimized || (draftText.trim().length === 0);

	const onCommentChange = useCallback(
		(updatedDraftProfileComment) => updateDraft(userid, draftText, updatedDraftProfileComment),
		[userid, draftText, updateDraft]
	);

	const onTextChange = useCallback(
		(updatedDraftBody) => updateDraft(userid, updatedDraftBody, draftComment),
		[userid, draftComment, updateDraft]
	);

	const closeConversationForUser = useCallback(
		() => closeConversation(userid),
		[userid, closeConversation],
	);

	const connectionLayerExperimentGroup = Context.getSetting("CONNECTION_LAYER_WEB");

	const showConnectionLayer = shouldShowConnectionLayer({
		connectionLayerExperimentGroup,
		likedThisSession,
		hasSentAMessage,
		canMessage,
		targetUser: user,
		platform: process.env.PLATFORM,
	});

	const connectionLayerProps = showConnectionLayer
		? mapDraftToConnectionLayer({ draft })
		: {};

	if (showConnectionLayer) {
		return (
			<Overlay
				siblings={(
					<div className="messenger-main-connection-window">
						<ConnectionsLayer
							{...connectionLayerProps}
							targetUser={user}
							onClose={closeConversationForUser}
							onMessageEdit={onTextChange}
							draft={draftText}
							sendMessage={sendMessage}
							sentMessage={messageISent && messageISent.body}
						/>
					</div>
				)}
			/>
		);
	}

	return (
		<div className={mainWindowClasses}>
			<MessengerUserRow
				className="messenger-main-window-user-row"
				onOpen={isMinimized ? openConversation : null}
				onMinimize={!isMinimized ? minimizeConversation : null}
				onClose={closeConversation}
				unreadCount={unreadCount}
				showUserMenuButton={!isMinimized}
				{...user}
			/>
			<div className="messenger-main-window-container">
				{hasMessages && !showOcsBlock ? (
					<MessagePane
						state={state}
						userid={userid}
						messages={messages}
						total={totalMessages}
						loading={loadingMessages}
						loadMessages={loadMessages}

						showUserBlock={showUserBlock}
						showContentWarning={showContentWarning}
						onDismissContentWarning={onDismissContentWarning}
						moveToSMSLoading={moveToSMSLoading}
						showMoveToSMS={hasMessages && canShowSend && showMoveToSMS}
						onMoveToSMS={onMoveToSMS}
						onReport={onReport}
						onUnblock={onUnblock}
					/>
				) : (
					<EmptyState
						showOcsBlock={showOcsBlock}
						showInstructions={!hasMessages && canShowSend}

						userInstructions={userInstructions}
						likedThisSession={likedThisSession}
						profileComment={draftComment}
					/>
				)}

				<Banners
					userid={userid}
					name={username}
					error={error}
					showFullInboxWarning={showFullInboxWarning}
					showScammerWarning={showScammerWarning}
					showFeedbackAgentWarning={showFeedbackAgentWarning}
					showInactiveWarning={showInactiveWarning}
				/>

				{canShowSend && (
					<React.Fragment>
						<Attachments
							comment={hasMessages ? draftComment : null}
							onCommentChange={onCommentChange}
						/>
						<Composer
							userid={userid}
							state={state}
							text={draftText}
							placeholder={isDesktop ? _str("Press enter to send") : _str("Say something...")}
							enterToSend={isDesktop}
							onTextChange={onTextChange}
							sendDisabled={isSendDisabled}
							sendMessage={sendMessage}
						/>
						<Toolbar
							userid={userid}
							sendDisabled={isSendDisabled}
							sendMessage={sendMessage}
						/>
					</React.Fragment>
				)}
			</div>
		</div>
	);
};

MainWindow.propTypes = {
	error: PropTypes.string,
	draft: PropTypes.shape({
		body: PropTypes.string,
		profileComment: PropTypes.shape({
			comment_type: PropTypes.string.isRequired,
			comment_content: PropTypes.object.isRequired,
		}),
	}),
	state: PropTypes.oneOf(Object.values(WINDOW_STATE)).isRequired,
	messages: PropTypes.arrayOf(PropTypes.shape({
		timestamp: PropTypes.number.isRequired,
	})),
	unreadCount: PropTypes.number.isRequired,
	totalMessages: PropTypes.number.isRequired,
	loadingMessages: PropTypes.bool.isRequired,
	nway: PropTypes.number.isRequired,
	fields: PropTypes.shape({
		userid: PropTypes.string.isRequired,
		userinfo: PropTypes.shape({
			displayname: PropTypes.string.isRequired,
		}).isRequired,
		likes: PropTypes.shape({
			mutual_like: PropTypes.number.isRequired,
		}).isRequired,
	}).isRequired,
	canMessage: PropTypes.bool.isRequired,
	hasInboxCapacity: PropTypes.bool.isRequired,
	userInstructions: PropTypes.object.isRequired,
	likedThisSession: PropTypes.bool.isRequired,
	feedbackAgent: PropTypes.bool.isRequired,
	showScammerWarning: PropTypes.bool.isRequired,
	showContentWarning: PropTypes.bool.isRequired,
	showMoveToSMS: PropTypes.bool.isRequired,
	moveToSMSLoading: PropTypes.bool.isRequired,

	openConversation: PropTypes.func,
	minimizeConversation: PropTypes.func,
	closeConversation: PropTypes.func,
	loadMessages: PropTypes.func.isRequired,
	updateDraft: PropTypes.func.isRequired,
	sendMessage: PropTypes.func.isRequired,
	onReport: PropTypes.func.isRequired,
	onUnblock: PropTypes.func.isRequired,
	onDismissContentWarning: PropTypes.func.isRequired,
	onMoveToSMS: PropTypes.func.isRequired,
};

MainWindow.defaultProps = {
	openConversation: null,
	minimizeConversation: null,
	closeConversation: null,
};

export default MainWindow;



// WEBPACK FOOTER //
// ./src/apps/messenger/components/MainWindow/index.jsx